import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Wrench, MapPin, Clock, Mail, Globe, Star, Phone, CheckCircle2, Zap, Loader2 } from "lucide-react";
import { apiFetch, fileUrl } from "@/lib/api";
import { PageSubtitle, Loader, EmptyState, Card } from "@/components/common";
import { cn } from "@/lib/utils";

export default function ServiceCategoryPage() {
  const { category } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogName, setCatalogName] = useState(location.state?.name || "");
  const [ratingFor, setRatingFor] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [savingRating, setSavingRating] = useState(false);
  const [rateError, setRateError] = useState("");

  const reload = () => apiFetch(`/services/${category}/providers`).then(setProviders).catch(() => {});

  useEffect(() => {
    if (!category) return;
    apiFetch("/services").then((list) => setCatalogName(list.find((s) => s.category === category)?.name ?? category)).catch(() => setCatalogName(category));
    apiFetch(`/services/${category}/providers`).then(setProviders).catch(() => {}).finally(() => setLoading(false));
  }, [category]);

  const submitRating = async (p) => {
    if (rating === 0) return;
    setSavingRating(true);
    setRateError("");
    try {
      await apiFetch(`/services/providers/${p.id}/reviews`, { method: "POST", body: JSON.stringify({ rating, comment: comment.trim() }) });
      setRatingFor(null);
      setRating(0);
      setComment("");
      reload();
    } catch (e) {
      setRateError(e.message || "Riprova.");
    } finally {
      setSavingRating(false);
    }
  };

  return (
    <div className="space-y-3">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-foreground">
        <ArrowLeft className="h-5 w-5" /> <span className="font-extrabold">Borsa Servizi</span>
      </button>
      <h1 data-testid="category-title" className="text-2xl font-extrabold text-foreground">{catalogName || category}</h1>
      <PageSubtitle>Fornitori inseriti e verificati dall'amministratore CargoFlow.</PageSubtitle>

      {loading ? (
        <Loader />
      ) : providers.length === 0 ? (
        <EmptyState icon={Wrench} title="Nessun fornitore" text="Nessun fornitore disponibile al momento in questa categoria." testId="category-empty" />
      ) : (
        providers.map((p) => (
          <Card key={p.id} data-testid={`provider-card-${p.id}`} className="space-y-2.5">
            <div className="flex items-center gap-3">
              {p.logo_id ? <ProviderLogo id={p.logo_id} /> : null}
              <div className="min-w-0 flex-1">
                <p className="truncate font-extrabold text-foreground">{p.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {[p.city, p.province ? `(${p.province})` : ""].filter(Boolean).join(" ") || "Zona non indicata"}
                  {p.area ? ` · ${p.area}` : ""}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {p.plan === "in_evidenza" ? <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-extrabold text-primary-foreground">In evidenza</span> : null}
                  {p.verified ? <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-extrabold text-secondary-foreground"><CheckCircle2 className="h-3 w-3" /> Verificato</span> : null}
                  {p.emergency_h24 ? <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-extrabold text-secondary-foreground"><Zap className="h-3 w-3" /> Emergenza H24</span> : null}
                </div>
              </div>
              {p.rating ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-2.5 py-1 text-[13px] font-extrabold text-foreground">
                  <Star className="h-3.5 w-3.5 text-[hsl(var(--warning))]" /> {p.rating}
                </span>
              ) : null}
            </div>
            {p.services_offered ? <InfoRow icon={Wrench} text={p.services_offered} /> : null}
            {p.address ? <InfoRow icon={MapPin} text={`${p.address}${p.city ? `, ${p.city}` : ""}`} /> : null}
            {p.hours ? <InfoRow icon={Clock} text={p.hours} /> : null}
            {p.email ? <a href={`mailto:${p.email}`}><InfoRow icon={Mail} text={p.email} /></a> : null}
            {p.website ? <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer"><InfoRow icon={Globe} text={p.website} /></a> : null}
            {p.notes ? <p className="border-t border-border pt-2 text-xs text-muted-foreground">{p.notes}</p> : null}

            <button data-testid={`provider-rate-${p.id}`} onClick={() => { setRatingFor(ratingFor === p.id ? null : p.id); setRating(0); setComment(""); setRateError(""); }} className="flex items-center gap-2 text-[13px] font-extrabold text-primary">
              <Star className="h-4 w-4" /> {p.rating_count ? `Valuta · ${p.rating_count} ${p.rating_count === 1 ? "recensione" : "recensioni"}` : "Valuta questo fornitore"}
            </button>
            {ratingFor === p.id ? (
              <div className="space-y-2 border-t border-border pt-2">
                <div className="flex gap-1.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button key={s} data-testid={`provider-star-${s}`} onClick={() => setRating(s)}>
                      <Star className={cn("h-7 w-7", s <= rating ? "fill-[hsl(var(--warning))] text-[hsl(var(--warning))]" : "text-[hsl(var(--warning))]")} />
                    </button>
                  ))}
                </div>
                <input data-testid="provider-review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commento (facoltativo)" className="min-h-[46px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-sm outline-none focus:border-primary" />
                {rateError ? <p className="text-xs text-destructive">{rateError}</p> : null}
                <button data-testid="provider-review-submit" onClick={() => submitRating(p)} disabled={savingRating || rating === 0} className="flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-[13px] font-extrabold text-primary-foreground disabled:opacity-60">
                  {savingRating ? <Loader2 className="h-4 w-4 animate-spin" /> : null} INVIA VALUTAZIONE
                </button>
              </div>
            ) : null}
            <a data-testid={`provider-call-${p.id}`} href={`tel:${(p.phone || "").replace(/\s/g, "")}`} className="flex min-h-[50px] items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground">
              <Phone className="h-5 w-5" /> Chiama · {p.phone}
            </a>
          </Card>
        ))
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="h-4 w-4 shrink-0 text-primary" />
      <span className="flex-1 text-[13px] font-semibold text-card-foreground">{text}</span>
    </div>
  );
}

function ProviderLogo({ id }) {
  const url = fileUrl(id);
  return <img data-testid={`provider-logo-${id}`} src={url} alt="logo" className="h-11 w-11 rounded-xl object-cover" />;
}
